<template>
	<div class="message-record-page">
		<div class="x-stat">
			<a-row>
				<a-col :span="3">
					<a-statistic title="今天" :value="stat.todayCount" suffix="条" />
				</a-col>
				<a-col :span="3">
					<a-statistic title="昨天" :value="stat.yesterdayCount" suffix="条" />
				</a-col>
				<a-col :span="3">
					<a-statistic title="近7平均" :value="stat.sevenDayAvg" suffix="条" />
				</a-col>
			</a-row>
		</div>

		<div class="record-panels">
			<a-card class="list-panel" :bordered="true" :body-style="{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }">
				<template slot="title">消息列表</template>
				<a-button slot="extra" type="primary" size="small" :loading="listLoading" @click="refreshList">刷新</a-button>

				<div class="list-body">
					<div class="message-list" ref="messageList" @scroll="onListScroll">
						<div
							v-for="item in messageList"
							:key="item.id"
							class="message-item"
							:class="{ active: selectedId === item.id }"
							@click="selectMessage(item)"
						>
							<div class="avatar-wrap">
								<img :src="item.fromUserAvatarUrl || ''" class="avatar" />
								<span v-if="item.isOnline === 'Y'" class="online-badge" title="在线"></span>
							</div>
							<div class="item-main">
								<div class="item-top">
									<span class="nickname">{{ item.fromUserNickname || '-' }}</span>
									<span class="time">{{ item.createdText || item.created || '' }}</span>
								</div>
								<div class="item-preview">
									<template v-if="item.type === 'PHOTO' || item.type === 'IMAGE'">
										<x-image
											v-if="getExtraValue(item.extra, 'imageUrl')"
											:src="getExtraValue(item.extra, 'imageUrl')"
											:width="56"
											:height="56"
											@click.native.stop
										/>
										<span v-else>[图片]</span>
									</template>
									<template v-else-if="item.type === 'VOICE'">
										<div @click.stop>
											<voice-message
												v-if="getExtraValue(item.extra, 'voiceUrl')"
												:msg-item="{
													content: getExtraValue(item.extra, 'voiceUrl'),
													duration: getExtraValue(item.extra, 'duration') || 0,
													isRead: 'Y',
												}"
												:is-self="false"
											/>
											<span v-else>[语音]</span>
										</div>
									</template>
									<template v-else-if="item.type === 'VIDEO'">
										<div @click.stop>
											<video-modal-player
												v-if="getExtraValue(item.extra, 'videoUrl')"
												:video-src="getExtraValue(item.extra, 'videoUrl')"
												:thumbnail="getExtraValue(item.extra, 'coverUrl') || getExtraValue(item.extra, 'videoUrl')"
												video-title="视频"
											/>
											<span v-else>[视频]</span>
										</div>
									</template>
									<span v-else-if="item.type === 'GIFT'">[礼物]</span>
									<span v-else-if="item.type === 'VOLTE' || item.type === 'VOLET'">{{ getCallPreview(item, '语音通话') }}</span>
									<span v-else-if="item.type === 'VILTE' || item.type === 'VILET'">{{ getCallPreview(item, '视频通话') }}</span>
									<span v-else>{{ item.content || '' }}</span>
								</div>
							</div>
						</div>

						<div class="list-footer">
							<span v-if="listLoading">加载中...</span>
							<span v-else-if="!hasMore && messageList.length">没有更多了</span>
							<span v-else-if="!messageList.length && !listLoading">暂无消息</span>
						</div>
					</div>

					<div
						v-show="showListBackTop"
						class="list-back-top"
						title="回到顶部"
						@click="scrollListToTop"
					>
						<a-icon type="to-top" />
					</div>
				</div>
			</a-card>

			<a-card class="chat-panel" :bordered="true" :body-style="{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }">
				<template slot="title">会话</template>

				<div v-if="!selectedConversationId" class="chat-empty">
					<a-empty description="请选择左侧消息查看会话" />
				</div>

				<template v-else>
					<div class="chat-users">
						<a-spin v-if="conversationLoading" class="chat-users-loading" />
						<template v-else>
							<div class="chat-user">
								<img :src="toUser.avatar || ''" class="user-avatar" />
								<div class="user-info">
									<div class="user-name">
										{{ toUser.nickname || '-' }}
										<span class="user-id">({{ toUser.id }})</span>
									</div>
									<div class="user-meta">
										{{ toUser.sex === 'MALE' ? '男' : (toUser.sex === 'FEMALE' ? '女' : '-') }}
										{{ toUser.age || '-' }}岁
										{{ toUser.province || '' }}{{ toUser.city || '' }}
									</div>
									<div class="user-asset">
										{{ toUser.sex === 'FEMALE' ? '积分' : '金币' }}:
										{{ toUser.sex === 'FEMALE' ? (toUser.score || 0) : (toUser.gold || 0) }}
									</div>
								</div>
							</div>
							<div class="chat-user-divider"></div>
							<div class="chat-user">
								<img :src="fromUser.avatar || ''" class="user-avatar" />
								<div class="user-info">
									<div class="user-name">
										{{ fromUser.nickname || '-' }}
										<span class="user-id">({{ fromUser.id }})</span>
									</div>
									<div class="user-meta">
										{{ fromUser.sex === 'MALE' ? '男' : (fromUser.sex === 'FEMALE' ? '女' : '-') }}
										{{ fromUser.age || '-' }}岁
										{{ fromUser.province || '' }}{{ fromUser.city || '' }}
									</div>
									<div class="user-asset">
										{{ fromUser.sex === 'FEMALE' ? '积分' : '金币' }}:
										{{ fromUser.sex === 'FEMALE' ? (fromUser.score || 0) : (fromUser.gold || 0) }}
									</div>
								</div>
							</div>
						</template>
					</div>

					<div class="chat-body">
						<conversation-message-drawer
							:key="'chat-' + chatNonce"
							:embedded="true"
							:lock-to-anchor="true"
							:conversation-id="selectedConversationId"
							:anchor-message-id="selectedId"
							:from-user-info="fromUser"
							:to-user-info="toUser"
						/>
					</div>
				</template>
			</a-card>
		</div>
	</div>
</template>

<script>
	import { messageList, messageStat, messageConversationUsers } from '@/api/op/message';
	import ConversationMessageDrawer from '@/components/ConversationMessageDrawer.vue';
	import XImage from '@/components/XImage.vue';
	import VoiceMessage from '@/components/VoiceMessage.vue';
	import VideoModalPlayer from '@/components/VideoModalPlayer.vue';

	const PAGE_SIZE = 20;

	export default {
		name: 'MessageList',
		components: {
			ConversationMessageDrawer,
			XImage,
			VoiceMessage,
			VideoModalPlayer,
		},
		data() {
			return {
				stat: {
					todayCount: 0,
					yesterdayCount: 0,
					sevenDayAvg: 0,
				},
				messageList: [],
				listLoading: false,
				pageIndex: 1,
				totalRow: 0,
				hasMore: true,
				selectedId: null,
				selectedConversationId: 0,
				chatNonce: 0,
				conversationLoading: false,
				showListBackTop: false,
				fromUser: {},
				toUser: {},
			};
		},
		created() {
			this.fetchStat();
			this.fetchList(true);
		},
		methods: {
			parseExtra(extra) {
				if (extra == null || String(extra).trim() === '') return {};
				if (typeof extra === 'object' && !Array.isArray(extra)) return extra;
				try {
					const parsed = JSON.parse(extra);
					return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
				} catch (e) {
					return {};
				}
			},
			getExtraValue(extra, key) {
				const obj = this.parseExtra(extra);
				const val = obj[key];
				return val == null ? '' : val;
			},
			formatCallDuration(seconds) {
				const sec = Number(seconds);
				if (!Number.isFinite(sec) || sec <= 0) return '';
				const mins = Math.floor(sec / 60);
				const secs = Math.floor(sec % 60);
				return `${mins}:${String(secs).padStart(2, '0')}`;
			},
			getHangupText(hangup) {
				const map = {
					1: '被叫拒绝',
					2: '被叫未接听',
					3: '主叫取消',
					4: '金币不足挂断',
					5: '违规中断',
				};
				return map[Number(hangup)] || '';
			},
			getCallPreview(item, label) {
				const callLen = this.getExtraValue(item.extra, 'callLen');
				const durationText = this.formatCallDuration(callLen);
				if (durationText) {
					return `[${label}] ${durationText}`;
				}
				const hangupText = this.getHangupText(this.getExtraValue(item.extra, 'hangup'));
				return hangupText ? `[${label}] ${hangupText}` : `[${label}]`;
			},
			mapUser(user) {
				if (!user) {
					return {};
				}
				return {
					id: user.id,
					nickname: user.nickname,
					avatar: user.avatarUrl || user.avatar || '',
					sex: user.sex,
					age: user.age,
					province: user.province,
					city: user.city,
					gold: user.gold,
					score: user.score,
				};
			},
			fetchStat() {
				messageStat().then((res) => {
					if (!res.isSucceed) return;
					this.stat = {
						todayCount: Number(res.data.todayCount) || 0,
						yesterdayCount: Number(res.data.yesterdayCount) || 0,
						sevenDayAvg: Number(res.data.sevenDayAvg) || 0,
					};
				}).catch(() => {});
			},
			refreshList() {
				this.fetchStat();
				this.fetchList(true);
			},
			fetchList(reset) {
				if (this.listLoading) return;
				if (!reset && !this.hasMore) return;

				if (reset) {
					this.pageIndex = 1;
					this.hasMore = true;
				}

				this.listLoading = true;
				messageList({
					page: this.pageIndex,
					pageSize: PAGE_SIZE,
				}).then((res) => {
					if (!res.isSucceed) {
						this.$message.error(res.message || '加载失败');
						return;
					}
					const list = (res.data && res.data.recordList) || [];
					this.totalRow = Number(res.data.totalRow) || 0;
					this.messageList = reset ? list : this.messageList.concat(list);
					this.hasMore = this.messageList.length < this.totalRow && list.length >= PAGE_SIZE;
					if (list.length) {
						this.pageIndex += 1;
					} else {
						this.hasMore = false;
					}
				}).catch(() => {
					this.$message.error('加载失败');
				}).finally(() => {
					this.listLoading = false;
				});
			},
			onListScroll(e) {
				const el = e.target;
				if (!el) return;
				this.showListBackTop = el.scrollTop > 200;
				if (this.listLoading || !this.hasMore) return;
				if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
					this.fetchList(false);
				}
			},
			scrollListToTop() {
				const el = this.$refs.messageList;
				if (!el) return;
				if (typeof el.scrollTo === 'function') {
					el.scrollTo({ top: 0, behavior: 'smooth' });
				} else {
					el.scrollTop = 0;
				}
				this.showListBackTop = false;
			},
			selectMessage(item) {
				const nextConversationId = Number(item.conversationId) || 0;
				const nextMessageId = Number(item.id) || 0;
				if (!nextConversationId || !nextMessageId) return;

				// 先写入发送方信息，保证右侧消息气泡能立刻按 from/to 渲染
				this.fromUser = {
					id: item.fromUserId,
					nickname: item.fromUserNickname || '-',
					avatar: item.fromUserAvatarUrl || '',
					sex: item.fromUserSex || '',
					age: item.fromUserAge || '',
					province: item.fromUserProvince || '',
					city: item.fromUserCity || '',
					gold: item.fromUserGold,
					score: item.fromUserScore,
				};
				this.toUser = {
					id: item.toUserId,
					nickname: (this.toUser && this.toUser.id === item.toUserId) ? this.toUser.nickname : '-',
					avatar: (this.toUser && this.toUser.id === item.toUserId) ? this.toUser.avatar : '',
					sex: (this.toUser && this.toUser.id === item.toUserId) ? this.toUser.sex : '',
					age: (this.toUser && this.toUser.id === item.toUserId) ? this.toUser.age : '',
					province: (this.toUser && this.toUser.id === item.toUserId) ? this.toUser.province : '',
					city: (this.toUser && this.toUser.id === item.toUserId) ? this.toUser.city : '',
					gold: (this.toUser && this.toUser.id === item.toUserId) ? this.toUser.gold : undefined,
					score: (this.toUser && this.toUser.id === item.toUserId) ? this.toUser.score : undefined,
				};

				this.selectedId = nextMessageId;
				this.selectedConversationId = nextConversationId;
				// 每次点击都换 key，强制右侧会话组件销毁重建并重新请求
				this.chatNonce += 1;
				this.conversationLoading = true;

				messageConversationUsers({
					conversationId: nextConversationId,
					fromUserId: item.fromUserId,
					toUserId: item.toUserId,
				}).then((res) => {
					if (!res.isSucceed) {
						this.$message.error(res.message || '加载会话失败');
						return;
					}
					this.fromUser = this.mapUser(res.data.fromUser);
					this.toUser = this.mapUser(res.data.toUser);
					const cid = Number(res.data.conversationId) || nextConversationId;
					if (cid) {
						this.selectedConversationId = cid;
					}
				}).catch(() => {
					this.$message.error('加载会话失败');
				}).finally(() => {
					this.conversationLoading = false;
				});
			},
		},
	};
</script>

<style lang="less" scoped>
	.message-record-page {
		/* 顶栏约 50px + PageView 上边距 16px + 底部间距 20px；负边距抵消布局 60px 占位 */
		height: calc(100vh - 88px);
		max-height: calc(100vh - 88px);
		margin-bottom: -60px;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		box-sizing: border-box;
	}

	.message-record-page ::v-deep .x-stat {
		flex-shrink: 0;
		margin-bottom: 8px;
		padding-top: 8px;
		padding-bottom: 8px;
	}

	.record-panels {
		display: flex;
		gap: 16px;
		flex: 1;
		min-height: 0;
		margin-top: 0;
	}

	.list-panel,
	.chat-panel {
		flex: 1;
		min-width: 0;
		height: 100%;
		display: flex;
		flex-direction: column;

		::v-deep .ant-card-body {
			flex: 1;
			min-height: 0;
			display: flex;
			flex-direction: column;
		}
	}

	.list-body {
		position: relative;
		flex: 1;
		min-height: 0;
		display: flex;
		flex-direction: column;
	}

	.message-list {
		flex: 1;
		overflow-y: auto;
		padding: 8px 0;
		min-height: 0;
	}

	.list-back-top {
		position: absolute;
		right: 16px;
		bottom: 24px;
		width: 40px;
		height: 40px;
		border-radius: 50%;
		background: #1890ff;
		color: #fff;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
		z-index: 5;
		transition: opacity 0.2s, transform 0.2s;

		&:hover {
			background: #40a9ff;
			transform: translateY(-2px);
		}
	}

	.message-item {
		display: flex;
		padding: 12px 16px;
		cursor: pointer;
		border-bottom: 1px solid #f0f0f0;
		transition: background 0.2s;

		&:hover,
		&.active {
			background: #e6f7ff;
		}
	}

	.avatar-wrap {
		position: relative;
		width: 48px;
		height: 48px;
		margin-right: 12px;
		flex-shrink: 0;
	}

	.avatar {
		width: 48px;
		height: 48px;
		border-radius: 4px;
		object-fit: cover;
		background: #f5f5f5;
	}

	.online-badge {
		position: absolute;
		right: -1px;
		bottom: -1px;
		width: 12px;
		height: 12px;
		background: #52c41a;
		border: 2px solid #fff;
		border-radius: 50%;
		box-shadow: 0 0 0 1px rgba(82, 196, 26, 0.2);
	}

	.item-main {
		flex: 1;
		min-width: 0;
	}

	.item-top {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 6px;
	}

	.nickname {
		font-weight: 600;
		color: #262626;
	}

	.time {
		color: #8c8c8c;
		font-size: 12px;
		margin-left: 8px;
		flex-shrink: 0;
	}

	.item-preview {
		color: #595959;
		font-size: 13px;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;

		::v-deep .x-image,
		::v-deep .cover-wrap,
		::v-deep .voice-content {
			display: inline-block;
			vertical-align: middle;
		}

		::v-deep .cover-img {
			width: 56px !important;
			height: 56px !important;
			object-fit: cover;
			border-radius: 4px;
		}

		::v-deep .cover-play-btn {
			transform: scale(0.7);
		}
	}

	.list-footer {
		text-align: center;
		color: #8c8c8c;
		font-size: 12px;
		padding: 12px 0 16px;
	}

	.chat-empty {
		flex: 1;
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 320px;
	}

	.chat-users {
		display: flex;
		align-items: stretch;
		padding: 5px 16px;
		border-bottom: 1px solid #f0f0f0;
		background: #fff;
		min-height: 72px;
		position: relative;
	}

	.chat-users-loading {
		margin: 16px auto;
	}

	.chat-user {
		flex: 1;
		display: flex;
		min-width: 0;
	}

	.chat-user-divider {
		width: 1px;
		background: #e8e8e8;
		margin: 0 16px;
	}

	.user-avatar {
		width: 56px;
		height: 56px;
		border-radius: 4px;
		object-fit: cover;
		margin-right: 12px;
		background: #f5f5f5;
		flex-shrink: 0;
	}

	.user-name {
		font-weight: 600;
		color: #262626;
		margin-bottom: 4px;
	}

	.user-id {
		color: #8c8c8c;
		font-weight: 400;
	}

	.user-meta {
		color: #8c8c8c;
		font-size: 12px;
		margin-bottom: 4px;
	}

	.user-asset {
		color: #faad14;
		font-size: 13px;
		font-weight: 600;
	}

	.chat-body {
		flex: 1;
		min-height: 0;
		overflow: hidden;
	}
</style>
